"use client";

import {
	type RefCallback,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	clampPanZoomPoint,
	clampPanZoomValue,
	type PanZoomViewportCenterOffset,
	resolveAnchoredPanZoomPoint,
	resolveContentPointAtViewportCenter,
	resolvePanToCenterContentPoint,
	resolvePanZoomBounds,
	scalePanZoomValue,
} from "./pan-zoom-math";
import { isPanZoomIgnoredTarget } from "./pan-zoom-ignore";

const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 8;
const DEFAULT_ZOOM_STEP = 0.25;
const DEFAULT_ZOOM_IN_MULTIPLIER = 2;
const DEFAULT_KEYBOARD_MOVE_DISTANCE = 50;
const DEFAULT_DOUBLE_TAP_DELAY = 300;
const DEFAULT_DOUBLE_CLICK_DELAY = 500;
const DEFAULT_DOUBLE_CLICK_MAX_STOPS = 2;
const DEFAULT_WHEEL_ZOOM_DISTANCE_FACTOR = 100;
const DEFAULT_PINCH_ZOOM_DISTANCE_FACTOR = 100;

interface ActivePointer {
	pointerId: number;
	clientX: number;
	clientY: number;
	pointerType: string;
}

interface PinchGestureState {
	previousDistance: number;
}

interface WebKitGestureState {
	initialZoom: number;
}

interface ViewportSize {
	width: number;
	height: number;
}

interface GestureLikeEvent extends Event {
	scale?: number;
	clientX?: number;
	clientY?: number;
	pageX?: number;
	pageY?: number;
}

export interface SetPanZoomTargetOptions {
	anchorClientX?: number;
	anchorClientY?: number;
	rapid?: boolean;
}

export interface UsePanZoomSurfaceOptions {
	contentWidth: number;
	contentHeight: number;
	/**
	 * `fixed` — use contentWidth/Height (2D plate SoT).
	 * `viewport` — content box tracks the measured viewport (iso full-bleed;
	 * zoom/pan still CSS-transform the content inside a fixed viewer unless
	 * {@link transformMode} is `camera`).
	 */
	contentFit?: "fixed" | "viewport";
	/**
	 * `css` (default) — CSS translate + scale (2D plate).
	 * `camera` — keep CSS identity; consumers (iso WebGL) dolly/pan the camera.
	 * Pan/zoom state + bounds still use virtual scale so gestures match.
	 */
	transformMode?: "css" | "camera";
	/**
	 * Extra equal-side pan slack (px). Iso full-bleed uses this so min-zoom
	 * can still reveal bottom/side corners with the same outer padding.
	 */
	panPaddingPx?: number;
	baseScale: number;
	minZoom?: number;
	maxZoom?: number;
	/** Starting zoom (clamped to min/max). Default 1. */
	initialZoom?: number;
	zoomStep?: number;
	zoomInMultiplier?: number;
	keyboardMoveDistance?: number;
	doubleTapDelay?: number;
	doubleClickDelay?: number;
	doubleClickMaxStops?: number;
	wheelZoomDistanceFactor?: number;
	pinchZoomDistanceFactor?: number;
	scrollToZoom?: boolean;
	reducedMotion?: boolean;
	transitionDurationMs?: number;
	/** When false, wheel/pan/pinch on the viewport are ignored. */
	interactionsEnabled?: boolean;
	/**
	 * When false, pointer-drag panning is disabled (wheel zoom / pinch still
	 * follow `interactionsEnabled`). Use for Select tool so canvas clicks
	 * select content instead of panning.
	 */
	panDragEnabled?: boolean;
}

export interface PanToCenterContentPointOptions {
	animate?: boolean;
	viewportCenterOffset?: PanZoomViewportCenterOffset;
}

export interface UsePanZoomSurfaceResult {
	zoom: number;
	scale: number;
	pan: { x: number; y: number };
	zoomStep: number;
	canPanX: boolean;
	canPanY: boolean;
	viewportSize: ViewportSize;
	contentStyle: {
		width: number;
		height: number;
		transform: string;
		transformOrigin: string;
		transition: string | undefined;
		willChange: string;
	};
	setViewportRef: RefCallback<HTMLDivElement>;
	setContentRef: RefCallback<HTMLDivElement>;
	setZoom: (targetZoom: number, options?: SetPanZoomTargetOptions) => void;
	panToCenterContentPoint: (
		contentPoint: { x: number; y: number },
		options?: PanToCenterContentPointOptions,
	) => void;
	/** Pan and zoom in one update so centering uses the final scale (avoids stale pan anchors). */
	panAndZoomToCenterContentPoint: (
		contentPoint: { x: number; y: number },
		targetZoom: number,
		options?: PanToCenterContentPointOptions,
	) => void;
	/** Content-space point currently under the viewport center (free-pan → follow seed). */
	getContentPointAtViewportCenter: () => { x: number; y: number };
	/** Current pan (px) — iso camera follow reads/writes this directly. */
	getPan: () => { x: number; y: number };
	/**
	 * Set pan immediately (clamped). Iso Play follow uses this with
	 * world→pan inverse under `transformMode="camera"`.
	 */
	setPanImmediate: (
		next: { x: number; y: number },
		options?: { animate?: boolean },
	) => void;
	/** PanZoom viewport element — interaction host for elevated UI overlays. */
	getViewportElement: () => HTMLElement | null;
	zoomIn: () => void;
	zoomOut: () => void;
	/**
	 * Toolbar/reset ease window (camera mode). False for wheel/pinch rapid + follow snap.
	 */
	animateTransform: boolean;
	transitionDurationMs: number;
}

export function usePanZoomSurface(
	options: UsePanZoomSurfaceOptions,
): UsePanZoomSurfaceResult {
	const {
		contentWidth,
		contentHeight,
		contentFit = "fixed",
		transformMode = "css",
		panPaddingPx = 0,
		baseScale,
		minZoom: minZoomOption = DEFAULT_MIN_ZOOM,
		maxZoom = DEFAULT_MAX_ZOOM,
		initialZoom: initialZoomOption,
		zoomStep = DEFAULT_ZOOM_STEP,
		zoomInMultiplier = DEFAULT_ZOOM_IN_MULTIPLIER,
		keyboardMoveDistance = DEFAULT_KEYBOARD_MOVE_DISTANCE,
		doubleTapDelay = DEFAULT_DOUBLE_TAP_DELAY,
		doubleClickDelay = DEFAULT_DOUBLE_CLICK_DELAY,
		doubleClickMaxStops = DEFAULT_DOUBLE_CLICK_MAX_STOPS,
		wheelZoomDistanceFactor = DEFAULT_WHEEL_ZOOM_DISTANCE_FACTOR,
		pinchZoomDistanceFactor = DEFAULT_PINCH_ZOOM_DISTANCE_FACTOR,
		scrollToZoom = false,
		reducedMotion = false,
		transitionDurationMs = 200,
		interactionsEnabled = true,
		panDragEnabled = true,
	} = options;

	const viewportRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const activePointersRef = useRef<ActivePointer[]>([]);
	const lastPointerDownRef = useRef(0);
	const pinchStateRef = useRef<PinchGestureState | null>(null);
	const webKitGestureStateRef = useRef<WebKitGestureState | null>(null);
	const transitionTimeoutRef = useRef<number | null>(null);

	const [viewportSize, setViewportSize] = useState<ViewportSize>({
		width: 0,
		height: 0,
	});
	/**
	 * Viewport-fit (iso full-bleed): content box == viewer. Scale must never
	 * go below 1 — otherwise the content shrinks and letterboxes the page.
	 * Zoom-out from a zoomed-in state returns to full-bleed; further zoom-out
	 * is a no-op (fixed viewer). Plate `fixed` keeps author minZoom (e.g. 0.5).
	 */
	const minZoom =
		contentFit === "viewport"
			? Math.max(1, minZoomOption)
			: minZoomOption;
	const resolvedInitialZoom = clampPanZoomValue(
		typeof initialZoomOption === "number" &&
			Number.isFinite(initialZoomOption)
			? initialZoomOption
			: 1,
		minZoom,
		maxZoom,
	);
	const [zoom, setZoomState] = useState(resolvedInitialZoom);
	const [pan, setPan] = useState({ x: 0, y: 0 });
	const [animateTransform, setAnimateTransform] = useState(false);

	/** Iso full-bleed: content box == viewer; plate keeps fixed content box. */
	const resolvedContentWidth =
		contentFit === "viewport" && viewportSize.width > 0
			? viewportSize.width
			: contentWidth;
	const resolvedContentHeight =
		contentFit === "viewport" && viewportSize.height > 0
			? viewportSize.height
			: contentHeight;

	const scale = baseScale * zoom;
	const bounds = useMemo(
		() =>
			resolvePanZoomBounds({
				contentWidth: resolvedContentWidth,
				contentHeight: resolvedContentHeight,
				viewportWidth: viewportSize.width,
				viewportHeight: viewportSize.height,
				scale,
				panPaddingPx,
				panExtent: transformMode === "camera" ? "frustum" : "overflow",
			}),
		[
			panPaddingPx,
			resolvedContentHeight,
			resolvedContentWidth,
			scale,
			transformMode,
			viewportSize.height,
			viewportSize.width,
		],
	);

	const interactionsEnabledRef = useRef(interactionsEnabled);
	useEffect(() => {
		interactionsEnabledRef.current = interactionsEnabled;
	}, [interactionsEnabled]);
	const panDragEnabledRef = useRef(panDragEnabled);
	useEffect(() => {
		panDragEnabledRef.current = panDragEnabled;
	}, [panDragEnabled]);

	const latestStateRef = useRef({
		baseScale,
		contentWidth: resolvedContentWidth,
		contentHeight: resolvedContentHeight,
		minZoom,
		maxZoom,
		zoomStep,
		zoomInMultiplier,
		keyboardMoveDistance,
		doubleTapDelay,
		doubleClickDelay,
		doubleClickMaxStops,
		wheelZoomDistanceFactor,
		pinchZoomDistanceFactor,
		scrollToZoom,
		reducedMotion,
		transitionDurationMs,
		viewportSize,
		zoom,
		scale,
		pan,
		bounds,
	});

	// Keep in sync during render so layout effects / rAF handlers see the current scale.
	latestStateRef.current = {
		baseScale,
		contentWidth: resolvedContentWidth,
		contentHeight: resolvedContentHeight,
		minZoom,
		maxZoom,
		zoomStep,
		zoomInMultiplier,
		keyboardMoveDistance,
		doubleTapDelay,
		doubleClickDelay,
		doubleClickMaxStops,
		wheelZoomDistanceFactor,
		pinchZoomDistanceFactor,
		scrollToZoom,
		reducedMotion,
		transitionDurationMs,
		viewportSize,
		zoom,
		scale,
		pan,
		bounds,
	};

	const clearTransformAnimation = useCallback(() => {
		if (transitionTimeoutRef.current != null) {
			window.clearTimeout(transitionTimeoutRef.current);
			transitionTimeoutRef.current = null;
		}
		setAnimateTransform(false);
	}, []);

	const scheduleTransformAnimation = useCallback(() => {
		if (reducedMotion || transitionDurationMs <= 0) {
			clearTransformAnimation();
			return;
		}
		if (transitionTimeoutRef.current != null) {
			window.clearTimeout(transitionTimeoutRef.current);
		}
		setAnimateTransform(true);
		transitionTimeoutRef.current = window.setTimeout(() => {
			setAnimateTransform(false);
			transitionTimeoutRef.current = null;
		}, transitionDurationMs + 34);
	}, [clearTransformAnimation, reducedMotion, transitionDurationMs]);

	const clampPointForScale = useCallback(
		(point: { x: number; y: number }, nextScale: number) =>
			clampPanZoomPoint(
				point,
				resolvePanZoomBounds({
					contentWidth: resolvedContentWidth,
					contentHeight: resolvedContentHeight,
					viewportWidth: latestStateRef.current.viewportSize.width,
					viewportHeight: latestStateRef.current.viewportSize.height,
					scale: nextScale,
					panPaddingPx,
					/**
					 * Must match live `bounds` — camera mode uses frustum extent.
					 * Overflow clamp here snapped Play follow release at edges
					 * (liveLookAt past overflow max → setPanImmediate yanked inward).
					 */
					panExtent:
						transformMode === "camera" ? "frustum" : "overflow",
				}),
			),
		[
			panPaddingPx,
			resolvedContentHeight,
			resolvedContentWidth,
			transformMode,
		],
	);

	/** If minZoom rises (e.g. viewport-fit floor at 1), pull zoom up — no letterbox. */
	useEffect(() => {
		if (zoom + 1e-6 >= minZoom) {
			return;
		}
		setZoomState(minZoom);
		setPan({ x: 0, y: 0 });
	}, [minZoom, zoom]);

	const setZoom = useCallback(
		(targetZoom: number, options?: SetPanZoomTargetOptions) => {
			const state = latestStateRef.current;
			const normalizedZoom = clampPanZoomValue(
				targetZoom,
				state.minZoom,
				state.maxZoom,
			);
			if (Math.abs(normalizedZoom - state.zoom) < 0.0001) {
				return;
			}

			const nextScale = state.baseScale * normalizedZoom;
			let nextPan = state.pan;
			const viewport = viewportRef.current;

			if (viewport) {
				const rect = viewport.getBoundingClientRect();
				const anchorClientX =
					options?.anchorClientX ?? rect.left + rect.width / 2;
				const anchorClientY =
					options?.anchorClientY ?? rect.top + rect.height / 2;
				const anchorX = anchorClientX - rect.left - rect.width / 2;
				const anchorY = anchorClientY - rect.top - rect.height / 2;
				nextPan = resolveAnchoredPanZoomPoint({
					anchorX,
					anchorY,
					fromScale: state.scale,
					toScale: nextScale,
					panX: state.pan.x,
					panY: state.pan.y,
				});
			}

			nextPan = clampPointForScale(nextPan, nextScale);
			if (options?.rapid) {
				clearTransformAnimation();
			} else {
				scheduleTransformAnimation();
			}

			setPan(nextPan);
			setZoomState(normalizedZoom);
		},
		[
			clampPointForScale,
			clearTransformAnimation,
			scheduleTransformAnimation,
		],
	);

	const panByDelta = useCallback(
		(deltaX: number, deltaY: number) => {
			const state = latestStateRef.current;
			const nextPan = clampPointForScale(
				{
					x: state.pan.x - deltaX,
					y: state.pan.y - deltaY,
				},
				state.scale,
			);
			clearTransformAnimation();
			setPan(nextPan);
		},
		[clampPointForScale, clearTransformAnimation],
	);

	const zoomIn = useCallback(() => {
		const state = latestStateRef.current;
		const targetZoom = state.zoom + state.zoomStep;
		setZoom(targetZoom);
	}, [setZoom]);

	const zoomOut = useCallback(() => {
		const state = latestStateRef.current;
		const targetZoom = state.zoom - state.zoomStep;
		setZoom(targetZoom);
	}, [setZoom]);

	const panToCenterContentPoint = useCallback(
		(
			contentPoint: { x: number; y: number },
			options?: PanToCenterContentPointOptions,
		) => {
			const state = latestStateRef.current;
			const nextPan = clampPointForScale(
				resolvePanToCenterContentPoint({
					contentPoint,
					contentWidth: state.contentWidth,
					contentHeight: state.contentHeight,
					scale: state.scale,
					viewportCenterOffset: options?.viewportCenterOffset,
				}),
				state.scale,
			);
			if (options?.animate === false) {
				clearTransformAnimation();
			} else {
				scheduleTransformAnimation();
			}
			setPan(nextPan);
		},
		[
			clampPointForScale,
			clearTransformAnimation,
			scheduleTransformAnimation,
		],
	);

	const getContentPointAtViewportCenter = useCallback(() => {
		const state = latestStateRef.current;
		return resolveContentPointAtViewportCenter({
			pan: state.pan,
			contentWidth: state.contentWidth,
			contentHeight: state.contentHeight,
			scale: state.scale,
		});
	}, []);

	const getPan = useCallback(() => {
		const { x, y } = latestStateRef.current.pan;
		return { x, y };
	}, []);

	const setPanImmediate = useCallback(
		(
			next: { x: number; y: number },
			options?: { animate?: boolean },
		) => {
			const state = latestStateRef.current;
			const clamped = clampPointForScale(next, state.scale);
			if (options?.animate === false) {
				clearTransformAnimation();
			} else {
				scheduleTransformAnimation();
			}
			setPan(clamped);
		},
		[
			clampPointForScale,
			clearTransformAnimation,
			scheduleTransformAnimation,
		],
	);

	const panAndZoomToCenterContentPoint = useCallback(
		(
			contentPoint: { x: number; y: number },
			targetZoom: number,
			options?: PanToCenterContentPointOptions,
		) => {
			const state = latestStateRef.current;
			const normalizedZoom = clampPanZoomValue(
				targetZoom,
				state.minZoom,
				state.maxZoom,
			);
			const nextScale = state.baseScale * normalizedZoom;
			const nextPan = clampPointForScale(
				resolvePanToCenterContentPoint({
					contentPoint,
					contentWidth: state.contentWidth,
					contentHeight: state.contentHeight,
					scale: nextScale,
					viewportCenterOffset: options?.viewportCenterOffset,
				}),
				nextScale,
			);
			if (options?.animate === false) {
				clearTransformAnimation();
			} else {
				scheduleTransformAnimation();
			}
			setPan(nextPan);
			setZoomState(normalizedZoom);
		},
		[
			clampPointForScale,
			clearTransformAnimation,
			scheduleTransformAnimation,
		],
	);

	const replacePointer = useCallback((event: PointerEvent) => {
		const nextPointer: ActivePointer = {
			pointerId: event.pointerId,
			clientX: event.clientX,
			clientY: event.clientY,
			pointerType: event.pointerType,
		};
		const pointers = activePointersRef.current.filter(
			(pointer) => pointer.pointerId !== event.pointerId,
		);
		pointers.push(nextPointer);
		activePointersRef.current = pointers;
	}, []);

	const clearPointer = useCallback((pointerId: number) => {
		activePointersRef.current = activePointersRef.current.filter(
			(pointer) => pointer.pointerId !== pointerId,
		);
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}

		const updateViewportSize = () => {
			setViewportSize((prev) => {
				const next = {
					width: viewport.clientWidth,
					height: viewport.clientHeight,
				};
				if (prev.width === next.width && prev.height === next.height) {
					return prev;
				}
				return next;
			});
		};

		updateViewportSize();
		const observer = new ResizeObserver(() => updateViewportSize());
		observer.observe(viewport);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setPan((prev) =>
			clampPointForScale(prev, latestStateRef.current.scale),
		);
	}, [clampPointForScale]);

	useEffect(() => {
		return () => {
			if (transitionTimeoutRef.current != null) {
				window.clearTimeout(transitionTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			const state = latestStateRef.current;
			const meta = event.metaKey || event.ctrlKey;
			const isPannable = state.bounds.canPanX || state.bounds.canPanY;

			const preventDefault = () => {
				event.preventDefault();
				event.stopPropagation();
			};

			if (isPannable) {
				if (event.key === "ArrowDown") {
					preventDefault();
					panByDelta(0, -state.keyboardMoveDistance);
					return;
				}
				if (event.key === "ArrowUp") {
					preventDefault();
					panByDelta(0, state.keyboardMoveDistance);
					return;
				}
				if (event.key === "ArrowLeft") {
					preventDefault();
					panByDelta(state.keyboardMoveDistance, 0);
					return;
				}
				if (event.key === "ArrowRight") {
					preventDefault();
					panByDelta(-state.keyboardMoveDistance, 0);
					return;
				}
			}

			if (event.key === "+" || (meta && event.key === "=")) {
				preventDefault();
				zoomIn();
				return;
			}
			if (event.key === "-" || (meta && event.key === "_")) {
				preventDefault();
				zoomOut();
				return;
			}
			if (meta && event.key === "0") {
				preventDefault();
				setZoom(1);
			}
		};

		const handleWheel = (event: WheelEvent) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			const state = latestStateRef.current;
			if (event.ctrlKey || event.metaKey || state.scrollToZoom) {
				const dominantDelta =
					Math.abs(event.deltaY) >= Math.abs(event.deltaX)
						? event.deltaY
						: event.deltaX;
				event.preventDefault();
				event.stopPropagation();
				setZoom(
					scalePanZoomValue(
						state.zoom,
						-dominantDelta,
						state.wheelZoomDistanceFactor,
					),
					{
						anchorClientX: event.clientX,
						anchorClientY: event.clientY,
						rapid: true,
					},
				);
				return;
			}

			if (state.bounds.canPanX || state.bounds.canPanY) {
				event.preventDefault();
				event.stopPropagation();
				panByDelta(event.deltaX, event.deltaY);
			}
		};

		const handlePointerDown = (event: PointerEvent) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			const contentNode = contentRef.current;
			if (
				(event.pointerType === "mouse" && event.buttons > 1) ||
				(contentNode && !contentNode.contains(event.target as Node)) ||
				isPanZoomIgnoredTarget(event.target)
			) {
				return;
			}

			viewport.focus({ preventScroll: true });

			const state = latestStateRef.current;
			const pointers = activePointersRef.current;
			const threshold =
				event.pointerType === "touch"
					? state.doubleTapDelay
					: state.doubleClickDelay;

			if (
				pointers.length === 0 &&
				event.timeStamp - lastPointerDownRef.current < threshold
			) {
				lastPointerDownRef.current = 0;
				const zoomStopFactor = Math.max(
					state.maxZoom ** (1 / state.doubleClickMaxStops),
					state.zoomInMultiplier,
				);
				const targetZoom =
					state.zoom >= 1
						? state.zoom !== state.maxZoom
							? state.zoom * zoomStopFactor
							: 1
						: state.zoom !== state.minZoom
							? state.zoom / zoomStopFactor
							: 1;
				event.preventDefault();
				event.stopPropagation();
				setZoom(targetZoom, {
					anchorClientX: event.clientX,
					anchorClientY: event.clientY,
					rapid: false,
				});
			} else {
				lastPointerDownRef.current = event.timeStamp;
			}

			/** Select tool: skip pointer-drag pan capture (wheel zoom still works). */
			if (!panDragEnabledRef.current) {
				return;
			}

			replacePointer(event);
			viewport.setPointerCapture?.(event.pointerId);

			if (activePointersRef.current.length === 2) {
				const [pointerA, pointerB] = activePointersRef.current;
				const initialDistance = Math.max(
					Math.hypot(
						pointerA.clientX - pointerB.clientX,
						pointerA.clientY - pointerB.clientY,
					),
					1,
				);
				pinchStateRef.current = {
					previousDistance: initialDistance,
				};
			}
		};

		const handlePointerMove = (event: PointerEvent) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			const state = latestStateRef.current;
			const pointers = activePointersRef.current;
			const activePointer = pointers.find(
				(pointer) => pointer.pointerId === event.pointerId,
			);

			if (pointers.length === 2 && pinchStateRef.current) {
				event.preventDefault();
				event.stopPropagation();
				replacePointer(event);
				const [pointerA, pointerB] = activePointersRef.current;
				const currentDistance = Math.hypot(
					pointerA.clientX - pointerB.clientX,
					pointerA.clientY - pointerB.clientY,
				);
				const targetZoom = scalePanZoomValue(
					state.zoom,
					currentDistance - pinchStateRef.current.previousDistance,
					state.pinchZoomDistanceFactor,
				);
				setZoom(targetZoom, {
					anchorClientX: (pointerA.clientX + pointerB.clientX) / 2,
					anchorClientY: (pointerA.clientY + pointerB.clientY) / 2,
					rapid: true,
				});
				pinchStateRef.current = {
					...pinchStateRef.current,
					previousDistance: currentDistance,
				};
				return;
			}

			if (!activePointer) {
				return;
			}

			if (
				panDragEnabledRef.current &&
				(state.bounds.canPanX || state.bounds.canPanY)
			) {
				event.preventDefault();
				event.stopPropagation();
				panByDelta(
					activePointer.clientX - event.clientX,
					activePointer.clientY - event.clientY,
				);
			}
			replacePointer(event);
		};

		const handlePointerUp = (event: PointerEvent) => {
			if (
				activePointersRef.current.length === 2 &&
				activePointersRef.current.some(
					(pointer) => pointer.pointerId === event.pointerId,
				)
			) {
				pinchStateRef.current = null;
			}
			clearPointer(event.pointerId);
			viewport.releasePointerCapture?.(event.pointerId);
		};

		const resolveGestureAnchor = (event: GestureLikeEvent) => {
			const viewportRect = viewport.getBoundingClientRect();
			const anchorClientX =
				event.clientX ??
				(event.pageX != null
					? event.pageX - window.scrollX
					: viewportRect.left + viewportRect.width / 2);
			const anchorClientY =
				event.clientY ??
				(event.pageY != null
					? event.pageY - window.scrollY
					: viewportRect.top + viewportRect.height / 2);
			return { anchorClientX, anchorClientY };
		};

		const handleGestureStart = (event: Event) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			webKitGestureStateRef.current = {
				initialZoom: latestStateRef.current.zoom,
			};
			event.preventDefault();
			event.stopPropagation();
		};

		const handleGestureChange = (event: Event) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			const gestureEvent = event as GestureLikeEvent;
			const state = latestStateRef.current;
			const initialZoom =
				webKitGestureStateRef.current?.initialZoom ?? state.zoom;
			const gestureScale =
				typeof gestureEvent.scale === "number" ? gestureEvent.scale : 1;
			const { anchorClientX, anchorClientY } =
				resolveGestureAnchor(gestureEvent);
			event.preventDefault();
			event.stopPropagation();
			setZoom(initialZoom * gestureScale, {
				anchorClientX,
				anchorClientY,
				rapid: true,
			});
		};

		const handleGestureEnd = (event: Event) => {
			if (!interactionsEnabledRef.current) {
				return;
			}
			webKitGestureStateRef.current = null;
			event.preventDefault();
			event.stopPropagation();
		};

		viewport.addEventListener("keydown", handleKeyDown);
		viewport.addEventListener("wheel", handleWheel, {
			passive: false,
			capture: true,
		});
		viewport.addEventListener("pointerdown", handlePointerDown);
		viewport.addEventListener("pointermove", handlePointerMove);
		viewport.addEventListener("pointerup", handlePointerUp);
		viewport.addEventListener("pointercancel", handlePointerUp);
		viewport.addEventListener("lostpointercapture", handlePointerUp);
		viewport.addEventListener("gesturestart", handleGestureStart, {
			passive: false,
			capture: true,
		});
		viewport.addEventListener("gesturechange", handleGestureChange, {
			passive: false,
			capture: true,
		});
		viewport.addEventListener("gestureend", handleGestureEnd, {
			passive: false,
			capture: true,
		});

		return () => {
			activePointersRef.current = [];
			lastPointerDownRef.current = 0;
			pinchStateRef.current = null;
			webKitGestureStateRef.current = null;
			viewport.removeEventListener("keydown", handleKeyDown);
			viewport.removeEventListener("wheel", handleWheel, true);
			viewport.removeEventListener("pointerdown", handlePointerDown);
			viewport.removeEventListener("pointermove", handlePointerMove);
			viewport.removeEventListener("pointerup", handlePointerUp);
			viewport.removeEventListener("pointercancel", handlePointerUp);
			viewport.removeEventListener("lostpointercapture", handlePointerUp);
			viewport.removeEventListener(
				"gesturestart",
				handleGestureStart,
				true,
			);
			viewport.removeEventListener(
				"gesturechange",
				handleGestureChange,
				true,
			);
			viewport.removeEventListener("gestureend", handleGestureEnd, true);
		};
	}, [clearPointer, panByDelta, replacePointer, setZoom, zoomIn, zoomOut]);

	const setViewportRef = useCallback((node: HTMLDivElement | null) => {
		viewportRef.current = node;
	}, []);

	const setContentRef = useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
	}, []);

	const getViewportElement = useCallback(
		() => viewportRef.current,
		[],
	);

	/**
	 * Camera mode: CSS stays identity (WebGL dollies). Virtual `scale` still
	 * drives pan bounds / follow math so gestures feel identical.
	 */
	const cssTransform =
		transformMode === "camera"
			? "translate3d(0px, 0px, 0) scale(1)"
			: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`;

	return {
		zoom,
		scale,
		pan,
		zoomStep,
		canPanX: bounds.canPanX,
		canPanY: bounds.canPanY,
		viewportSize,
		contentStyle: {
			width: resolvedContentWidth,
			height: resolvedContentHeight,
			transform: cssTransform,
			transformOrigin: "center center",
			transition:
				transformMode === "camera"
					? undefined
					: animateTransform
						? `transform ${transitionDurationMs}ms cubic-bezier(.22,1,.36,1)`
						: undefined,
			willChange: transformMode === "camera" ? "auto" : "transform",
		},
		/** Camera-mode toolbar ease — CSS stays identity; WebGL lerps while true. */
		animateTransform,
		transitionDurationMs,
		setViewportRef,
		setContentRef,
		setZoom,
		panToCenterContentPoint,
		panAndZoomToCenterContentPoint,
		getContentPointAtViewportCenter,
		getPan,
		setPanImmediate,
		getViewportElement,
		zoomIn,
		zoomOut,
	};
}
